import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Shield, FileText, AlertTriangle, Loader2, Smartphone, ArrowRight, CheckCircle2
} from 'lucide-react';

type Step = 'read' | 'phone' | 'vipps_polling' | 'brreg_check' | 'success' | 'error';

export default function DpaDelegatedSigningPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [dpaInfo, setDpaInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [step, setStep] = useState<Step>('read');
  const [cibaMessage, setCibaMessage] = useState('');
  const [vippsVerified, setVippsVerified] = useState<{full_name?: string; birth_date?: string} | null>(null);
  const [signatureId, setSignatureId] = useState<string | null>(null);
  const pollingRef = useRef<any>(null);
  const abortedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/dpa/sign/delegated/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Kunne ikke laste avtale');
        setDpaInfo(data);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      abortedRef.current = true;
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [token]);

  const startVipps = useCallback(async () => {
    setError(null);
    setStep('vipps_polling');
    try {
      const res = await fetch(`/api/dpa/sign/delegated/${token}/vipps-init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunne ikke starte Vipps');
      setCibaMessage(data.message || 'Sjekk Vipps-appen på telefonen din');
      pollVipps(data.auth_req_id, data.poll_interval || 5);
    } catch (e: any) {
      setError(e.message);
      setStep('phone');
    }
  }, [phoneNumber, token]);

  const pollVipps = useCallback((authReqId: string, startInterval: number) => {
    let currentInterval = startInterval;
    const tick = async () => {
      if (abortedRef.current) return;
      try {
        const res = await fetch(`/api/dpa/sign/delegated/${token}/vipps-status/${authReqId}`);
        const data = await res.json();
        if (abortedRef.current) return;
        if (data.status === 'pending') {
          if (data.slowDown) currentInterval = Math.min(currentInterval * 2, 60);
          pollingRef.current = setTimeout(tick, currentInterval * 1000);
          return;
        }
        pollingRef.current = null;
        if (data.status === 'complete') {
          setVippsVerified(data.display || null);
          finalize(authReqId);
        } else if (data.status === 'rejected') {
          setError('Du avviste Vipps-forespørselen');
          setStep('phone');
        } else if (data.status === 'expired') {
          setError('Vipps-forespørselen utløp — prøv igjen');
          setStep('phone');
        } else {
          setError(data.error || 'Vipps-verifisering feilet');
          setStep('phone');
        }
      } catch (e: any) {
        if (abortedRef.current) return;
        pollingRef.current = null;
        setError(e.message);
        setStep('phone');
      }
    };
    if (pollingRef.current) clearTimeout(pollingRef.current);
    pollingRef.current = setTimeout(tick, currentInterval * 1000);
  }, [token]);

  const finalize = useCallback(async (authReqId: string) => {
    setStep('brreg_check');
    try {
      const res = await fetch(`/api/dpa/sign/delegated/${token}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_req_id: authReqId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signering feilet');
      setSignatureId(data.signature.id);
      setStep('success');
    } catch (e: any) {
      setError(e.message);
      setStep('error');
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  if (error && !dpaInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md bg-white rounded-lg shadow p-6 border border-red-200">
          <AlertTriangle className="h-10 w-10 text-red-600 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-center text-red-900">Kunne ikke laste avtale</h1>
          <p className="text-sm text-gray-700 mt-2 text-center">{error}</p>
        </div>
      </div>
    );
  }

  if (!dpaInfo) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-6 w-6 text-kf-primary" />
            <h1 className="text-2xl font-bold text-kf-primary">Databehandleravtale — Delegert signering</h1>
          </div>
          <p className="text-gray-600">
            <strong>{dpaInfo.invited_by?.name}</strong> har bedt om at du signerer en databehandleravtale på vegne av <strong>{dpaInfo.party_name}</strong>
            {dpaInfo.orgnr && ` (org.nr. ${dpaInfo.orgnr})`}.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Avtaletype: <strong>{dpaInfo.dpa_type}</strong> · Versjon: {dpaInfo.template?.version}
          </p>
        </div>

        {dpaInfo.template?.is_draft && (
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-700 flex-shrink-0" />
              <div className="text-sm">
                <strong className="text-yellow-900">UTKAST — IKKE JURIDISK BINDENDE</strong>
                <p className="text-yellow-800 mt-1">
                  Denne signeringen er utført med utkast-mal under juridisk gjennomgang.
                  Prod-versjonen publiseres etter juridisk godkjenning og krever da ny signering.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5" /> Avtaleinnhold
          </h2>
          <div className="max-h-[60vh] overflow-y-auto bg-white border border-gray-200 rounded-md p-6 text-sm text-gray-800 leading-relaxed">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="text-xl font-bold text-kf-primary mt-4 mb-3 pb-2 border-b border-gray-200">{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-lg font-semibold text-kf-primary mt-5 mb-2">{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-base font-semibold text-gray-900 mt-4 mb-2 border-l-4 border-kf-primary pl-3">{children}</h3>
                ),
                h4: ({ children }) => (
                  <h4 className="text-sm font-semibold text-gray-800 mt-3 mb-1 uppercase tracking-wide">{children}</h4>
                ),
                p: ({ children }) => <p className="mb-3">{children}</p>,
                ol: ({ children }) => <ol className="list-decimal ml-6 mb-3 space-y-1.5">{children}</ol>,
                ul: ({ children }) => <ul className="list-disc ml-6 mb-3 space-y-1.5">{children}</ul>,
                li: ({ children }) => <li className="pl-1">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-kf-primary/40 bg-gray-50 pl-4 py-2 my-3 italic text-gray-700">{children}</blockquote>
                ),
                hr: () => <hr className="my-4 border-gray-200" />,
                a: ({ children, href }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-kf-primary underline hover:no-underline">{children}</a>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-3">
                    <table className="min-w-full border-collapse border border-gray-300 text-sm">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
                th: ({ children }) => (
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-900">{children}</th>
                ),
                td: ({ children }) => <td className="border border-gray-300 px-3 py-2">{children}</td>,
              }}
            >
              {dpaInfo.template?.content || ''}
            </ReactMarkdown>
          </div>
        </div>

        {(step === 'read' || step === 'phone') && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
              <Smartphone className="h-5 w-5" /> Signer med Vipps
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Skriv inn ditt mobilnummer. Vi verifiserer identiteten via Vipps og sjekker deretter automatisk at du har signaturrett i Brønnøysundregisteret for {dpaInfo.party_name}.
            </p>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>
            )}
            <div className="flex gap-3">
              <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                     placeholder="+47 xxx xx xxx"
                     className="flex-1 px-3 py-2 border border-gray-300 rounded" />
              <button onClick={startVipps} disabled={!phoneNumber}
                      className="px-6 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary-dark disabled:opacity-50 flex items-center gap-2">
                Start Vipps <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {(step === 'vipps_polling' || step === 'brreg_check') && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Loader2 className="h-12 w-12 text-kf-primary mx-auto animate-spin mb-4" />
            <h2 className="text-lg font-semibold text-gray-900">
              {step === 'vipps_polling' && 'Venter på bekreftelse i Vipps-appen'}
              {step === 'brreg_check' && 'Verifiserer signaturrett mot Brønnøysundregisteret og signerer PDF...'}
            </h2>
            {cibaMessage && step === 'vipps_polling' && <p className="text-sm text-gray-600 mt-2">{cibaMessage}</p>}
            <p className="text-xs text-gray-500 mt-4">Ikke lukk vinduet — flyten fullføres automatisk.</p>
          </div>
        )}

        {step === 'success' && (
          <div className="bg-white rounded-lg shadow-sm border border-green-200 p-8">
            <div className="text-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
              <h2 className="text-2xl font-bold text-gray-900 mt-3">Databehandleravtalen er signert</h2>
              {dpaInfo.template?.is_draft && <p className="text-sm text-yellow-800 mt-2 font-medium">Merk: signeringen er utført med UTKAST-mal. Endelig versjon krever ny signering etter juridisk gjennomgang.</p>}
            </div>
            <div className="text-sm text-gray-700 space-y-2 max-w-md mx-auto">
              <p>✓ Vipps-verifisert identitet: <strong>{vippsVerified?.full_name}</strong></p>
              <p>✓ Signaturrett bekreftet i Brønnøysundregisteret</p>
              <p>✓ PDF signert med Buypass eSeal og lagret</p>
              <p>✓ E-post-kvittering med PDF er sendt til organisasjonens kontakter</p>
            </div>
            {signatureId && (
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500 mb-2">Signatur-ID:</p>
                <p className="font-mono text-xs bg-gray-100 rounded p-2 inline-block">{signatureId}</p>
              </div>
            )}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center space-y-3">
              <p className="text-sm text-gray-600">
                Du kan nå trygt lukke dette vinduet. Ingen ytterligere handling kreves.
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <a
                  href="https://www.kompetanseforbundet.no"
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 inline-flex items-center gap-2 text-sm"
                >
                  Gå til kompetanseforbundet.no
                </a>
                <button
                  onClick={() => window.close()}
                  className="px-4 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-dark inline-flex items-center gap-2 text-sm"
                >
                  Lukk vindu
                </button>
              </div>
              <p className="text-xs text-gray-400 italic">
                (Hvis «Lukk vindu» ikke virker, kan du bare lukke fanen manuelt.)
              </p>
            </div>
          </div>
        )}

        {step === 'error' && (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
            <AlertTriangle className="h-10 w-10 text-red-600 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-center text-red-900">Signering feilet</h2>
            <p className="text-sm text-gray-700 mt-2 text-center">{error}</p>
            <div className="mt-4 text-center">
              <button onClick={() => { setError(null); setStep('phone'); }}
                      className="px-4 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary-dark">
                Prøv igjen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
